import type React from 'react';
import { useRef } from 'react';

import { cn } from '@change/ds-utils';

import type { PetitionMarqueeCardData } from '../../hooks/usePetitionMarquee';
import { PetitionMarqueeCard } from '../PetitionMarqueeCard';

import { useEasedHoverPause } from './hooks/useEasedHoverPause';
import { snapFocusedCardIntoView } from './snapFocusedCardIntoView';

type Props = Readonly<{
	petitions: readonly PetitionMarqueeCardData[];
	onCardClick: (petitionKey: string) => void;
	/**
	 * Playback state once the play/pause control owns it. `undefined` (SSR + pre-hydration
	 * paint, where the OS motion preference isn't knowable) defers pausing to the
	 * motion-reduce CSS instead.
	 */
	isPlaying: boolean | undefined;
	/** Run the loop in the opposite direction (the mobile top row). */
	reverse?: boolean;
	className?: string;
}>;

/**
 * One marquee line: the petitions render twice and the animate-petition-marquee keyframe
 * slides the track to -50%, where the second half lines up exactly where the first began.
 * Speed is the animation duration in index.css; hovering eases it to a standstill via
 * useEasedHoverPause.
 */
export const MarqueeTrack: React.FC<Props> = ({ petitions, onCardClick, isPlaying, reverse = false, className }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const trackRef = useRef<HTMLUListElement>(null);
	const hoverPause = useEasedHoverPause(trackRef);

	return (
		// Show fade on xtra wide screen widths
		<div
			ref={containerRef}
			onPointerEnter={hoverPause.onPointerEnter}
			onPointerLeave={hoverPause.onPointerLeave}
			className={cn(
				'group relative min-w-0 overflow-x-clip py-2',
				'min-[120rem]:[mask-image:linear-gradient(to_right,transparent,black_48px,black_calc(100%_-_48px),transparent)]',
				className,
			)}
		>
			<ul
				ref={trackRef}
				/* keyboard focus can land on a card the loop has carried out of the clipped window
				   (overflow-x-clip can't scroll) - seek the animation so the card is visible.
				   Mouse focus is excluded inside the handler so clicks aren't yanked mid-press */
				onFocus={(event) => {
					if (containerRef.current && trackRef.current) {
						snapFocusedCardIntoView({
							container: containerRef.current,
							track: trackRef.current,
							target: event.target,
							reverse,
						});
					}
				}}
				/* focus-within matters as much as hover: a keyboard user tabbing into a moving track
			   would otherwise watch the card they just focused slide out of the window */
				/* list-none strips list semantics in Safari/VoiceOver; role="list" restores them */
				role="list"
				className={cn(
					'animate-petition-marquee group-focus-within:paused m-0 flex w-max list-none p-0',
					/* the eased ramp owns hover once it can drive the animation; until then (SSR,
					   pre-hydration, no WAAPI) the CSS hard pause stands in */
					!hoverPause.active && 'group-hover:paused',
					/* only until the control takes over: once isPlaying is set it reflects the motion
				   preference (and any explicit override), so leaving the class on would let the CSS
				   pause a track the control announces as playing */
					isPlaying === undefined && 'motion-reduce:paused',
				)}
				style={{
					animationPlayState: isPlaying === false ? 'paused' : undefined,
					animationDirection: reverse ? 'reverse' : undefined,
				}}
				data-qa={`petition-marquee-track${reverse ? '-reverse' : ''}`}
			>
				{([false, true] as const).map((isClone) =>
					petitions.map((petition, index) => (
						<PetitionMarqueeCard
							/* index in the key: a short curation fills the row by repeating petitions */
							key={`${petition.key}-${index}-${isClone ? 'clone' : 'original'}`}
							petition={petition}
							onClick={() => onCardClick(petition.key)}
							/* the clone half + repeat fills are aria-hidden/untabbable so each petition is
							   announced and tabbed exactly once, but stay clickable - the loop scrolls them
							   into view. The accessible copies must be the first half in BOTH directions:
							   deep clone-half cards never enter the window, and only first-half cards are
							   reachable by the focus snap above */
							duplicate={isClone || petitions.findIndex(({ key }) => key === petition.key) !== index}
						/>
					)),
				)}
			</ul>
		</div>
	);
};
